/**
 * Prepares the official AmbirScan Web Connect service when the Visitors page
 * opens, then reads a name and the front of the ID from a scan.
 *
 * The service is the Ambir Scanner Bridge at https://localhost:53052. A failed
 * check leaves manual entry in place. No scan is invented.
 */

import { barcodesFromImage } from './pdf417Decode';

const SERVICE_URL = 'https://localhost:53052';

export const SCANNER_PREPARING_MESSAGE = 'Getting the ID scanner ready…';
export const SCANNER_READY_MESSAGE = 'Listening for an ID. Place it in the scanner.';
export const SCANNER_UNAVAILABLE_MESSAGE = 'ID scanner is not connected. Enter the visitor manually.';

// Both sides, same as before. The front is the photo and the back is the barcode.
// Which side is which stays in toVisitorScan.
export const ID_SCAN_PARAMS = {
  resolution: 600,
  colorMode: 'Color',
  duplexMode: 'DuplexShortEdge',
  pageSize: 'Auto',
  autoRotate: true,
  autoDeskew: true,
  autoCrop: true,
  outputFormat: 'jpeg',
  barcodeReadingEnabled: true,
  barcodeFilterLevel: 'Normal',
};

const SCAN_PARAMS = ID_SCAN_PARAMS;

const listeners = new Set();

const state = {
  status: 'idle',
  message: SCANNER_UNAVAILABLE_MESSAGE,
  lastScan: null,
  openCheckIn: false,
  sourceName: '',
  stream: null,
  batch: [],
  batchTimer: null,
  rearmTimer: null,
};

function snapshot() {
  return {
    status: state.status,
    message: state.message,
    lastScan: state.lastScan,
    openCheckIn: state.openCheckIn,
  };
}

function emit() {
  const current = snapshot();
  listeners.forEach((listener) => listener(current));
}

function setStatus(status, message) {
  state.status = status;
  state.message = message;
  emit();
}

export function subscribeIdScanner(listener) {
  listeners.add(listener);
  listener(snapshot());
  return () => listeners.delete(listener);
}

export function idScannerIsReady() {
  return state.status === 'ready';
}

export async function readIdScannerConnection({ fetchImpl = browserFetch() } = {}) {
  if (typeof fetchImpl !== 'function') return { connected: false };
  try {
    const response = await fetchWithTimeout(fetchImpl, `${SERVICE_URL}/api/twain/status`, {
      mode: 'cors',
      credentials: 'include',
    }, 1500);
    if (!response.ok) return { connected: false };
    const data = await response.json();
    if (!data?.serviceOnline) return { connected: false };
    const scanner = data.scannerStatus || {};
    if (scanner.currentSourceName || scanner.isSourceOpen || scanner.isScanning) {
      return { connected: true };
    }
    const sourcesResponse = await sendCommand(fetchImpl, 'getSources', null, 2500);
    const sources = sourcesResponse?.data?.availableSources || [];
    return { connected: sources.map(sourceName).some(Boolean) };
  } catch (error) {
    return { connected: false };
  }
}

function clean(value) {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

const SUFFIX_CASE = { JR: 'Jr', SR: 'Sr', II: 'II', III: 'III', IV: 'IV', V: 'V' };

function nameCase(value) {
  const text = clean(value);
  const letters = text.replace(/[^A-Za-z]/g, '');
  if (!letters || letters !== letters.toUpperCase()) return text;
  return text.toLowerCase().replace(/(^|[\s'-])([a-z])/g, (_match, boundary, letter) => `${boundary}${letter.toUpperCase()}`);
}

function suffixCase(value) {
  const text = clean(value).replace(/\./g, '').toUpperCase();
  if (!text) return '';
  return SUFFIX_CASE[text] || nameCase(value);
}

function middleNameCase(value) {
  const text = nameCase(value);
  if (!text || /^(none|nmn|n\/a|na)$/i.test(text)) return '';
  return text;
}

function readElement(source, code) {
  const match = source.match(new RegExp(`(?:^|\\n)\\s*${code}([^\\n\\r]*)`));
  return clean(match?.[1]);
}

function aamvaDate(value) {
  const text = clean(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
  const match = text.match(/^(\d{2})(\d{2})(\d{4})$/);
  if (!match) return '';
  const month = Number(match[1]);
  const day = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return '';
  return `${match[3]}-${match[1]}-${match[2]}`;
}

export function readAamvaName(value) {
  let record = value;
  if (typeof value === 'string') {
    const text = value.trim();
    if (text.startsWith('{')) {
      try {
        record = JSON.parse(text);
      } catch (error) {
        record = text;
      }
    }
  }

  if (record && typeof record === 'object') {
    const first = nameCase(record.firstName || record.givenName || record.DAC || record.DCT);
    const last = nameCase(record.lastName || record.familyName || record.DCS);
    const middle = middleNameCase(record.middleName || record.DAD);
    const suffix = suffixCase(record.suffix || record.DCU);
    const birthDate = aamvaDate(record.birthDate || record.dateOfBirth || record.DBB);
    return {
      fullName: [first, middle, last, suffix].filter(Boolean).join(' ') || nameCase(record.fullName || record.full_name),
      firstName: first,
      middleName: middle,
      lastName: last,
      suffix,
      birthDate,
      idNumber: clean(record.idNumber || record.licenseNumber || record.DAQ),
    };
  }

  const source = String(record || '');
  const first = nameCase(readElement(source, 'DAC') || readElement(source, 'DCT').split(',')[0]);
  const last = nameCase(readElement(source, 'DCS'));
  const middle = middleNameCase(readElement(source, 'DAD'));
  const suffix = suffixCase(readElement(source, 'DCU'));
  const birthDate = aamvaDate(readElement(source, 'DBB'));
  return {
    fullName: [first, middle, last, suffix].filter(Boolean).join(' '),
    firstName: first,
    middleName: middle,
    lastName: last,
    suffix,
    birthDate,
    idNumber: readElement(source, 'DAQ'),
  };
}

function dataUrlFor(image) {
  if (!image?.base64Data) return null;
  return `data:${image.mimeType || 'image/jpeg'};base64,${image.base64Data}`;
}

function imageHasPdf417(image) {
  return (image?.barcodes || []).some((barcode) => /pdf.?417/i.test(barcode?.barcodeType || '') || barcode?.isAamva);
}

function barcodeHasIdentity(barcodes) {
  return (barcodes || []).some((barcode) => /DCS|DAC|DBB/.test(String(barcode?.parsedData || barcode?.text || '')));
}

async function hashIdNumber(idNumber) {
  const value = clean(idNumber);
  const subtle = typeof window !== 'undefined' ? window.crypto?.subtle : null;
  if (!value || !subtle) return null;
  const digest = await subtle.digest('SHA-256', new TextEncoder().encode(value));
  return Array.from(new Uint8Array(digest)).map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

export async function toVisitorScan(images) {
  const list = Array.isArray(images) ? images : [];
  const identity = {
    fullName: '', firstName: '', middleName: '', lastName: '', suffix: '', birthDate: '',
  };
  let idNumber = '';
  list.forEach((image) => {
    const barcodes = Array.isArray(image?.barcodes) ? image.barcodes : [];
    barcodes.forEach((barcode) => {
      const parsed = readAamvaName(barcode?.parsedData || barcode?.text || '');
      if (!identity.fullName && parsed.fullName) identity.fullName = parsed.fullName;
      if (!identity.firstName && parsed.firstName) identity.firstName = parsed.firstName;
      if (!identity.middleName && parsed.middleName) identity.middleName = parsed.middleName;
      if (!identity.lastName && parsed.lastName) identity.lastName = parsed.lastName;
      if (!identity.suffix && parsed.suffix) identity.suffix = parsed.suffix;
      if (!identity.birthDate && parsed.birthDate) identity.birthDate = parsed.birthDate;
      if (!idNumber && parsed.idNumber) idNumber = parsed.idNumber;
    });
  });

  const pages = list.filter((image) => image?.base64Data);
  const barcodePage = pages.find((image) => imageHasPdf417(image)) || null;
  const otherPage = pages.find((image) => image !== barcodePage) || null;
  let front = otherPage;
  let back = barcodePage;
  if (!barcodePage && pages.length > 1) {
    const backFirst = pages.some((image) => image.duplexBackFirst);
    back = backFirst ? pages[0] : pages[1];
    front = backFirst ? pages[1] : pages[0];
  } else if (!front) {
    front = pages[0] || null;
  }
  const imageDataUrl = dataUrlFor(front);
  const backImageDataUrl = dataUrlFor(back);
  const documentToken = await hashIdNumber(idNumber);
  const scanned = Boolean(identity.fullName || imageDataUrl);
  const extracted = Boolean(identity.firstName && identity.lastName && identity.birthDate);
  return {
    available: true,
    scanned,
    ...identity,
    identityExtracted: extracted,
    documentToken,
    imageDataUrl,
    frontImageDataUrl: imageDataUrl,
    backImageDataUrl,
    message: scanned
      ? (extracted
        ? 'Scan received. Check the name and birth date before screening.'
        : 'Manual verification: the scanner did not return a complete name and birth date. Enter them from the ID.')
      : 'The scanner did not return a name or ID image. Enter the visitor manually.',
  };
}

function browserFetch() {
  if (typeof window !== 'undefined' && typeof window.fetch === 'function') return window.fetch.bind(window);
  return null;
}

async function fetchWithTimeout(fetchImpl, url, options, timeoutMs) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetchImpl(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

async function serviceIsOnline(fetchImpl) {
  try {
    const response = await fetchWithTimeout(fetchImpl, `${SERVICE_URL}/api/twain/status`, {
      mode: 'cors',
      credentials: 'include',
    }, 1500);
    if (!response.ok) return false;
    const data = await response.json();
    return Boolean(data.serviceOnline && data.desktopAppConnected);
  } catch (error) {
    return false;
  }
}

function sourceName(source) {
  if (typeof source === 'string') return source;
  return source?.name || source?.sourceName || '';
}

async function sendCommand(fetchImpl, method, params, timeoutMs) {
  const response = await fetchWithTimeout(fetchImpl, `${SERVICE_URL}/api/twain`, {
    method: 'POST',
    mode: 'cors',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      kind: 'twainlocalscanner',
      method,
      params: params ?? null,
    }),
  }, timeoutMs);
  if (!response.ok) throw new Error('The ID scanner did not accept the request.');
  const data = await response.json();
  if (data?.results && data.results.success === false) {
    throw new Error(data.data?.message || 'The ID scanner did not accept the request.');
  }
  return data;
}

function publishScan(scan) {
  state.lastScan = { ...scan, id: `${Date.now()}` };
  state.openCheckIn = true;
  emit();
}

export function acknowledgeVisitorScan() {
  state.openCheckIn = false;
}

async function imagesWithBarcodes(images) {
  const list = Array.isArray(images) ? images : [];
  const decoded = [];
  let foundIdentity = false;
  for (const image of list) {
    const barcodes = foundIdentity
      ? (Array.isArray(image?.barcodes) ? image.barcodes : [])
      : await barcodesFromImage(image);
    if (barcodeHasIdentity(barcodes)) foundIdentity = true;
    decoded.push({ ...image, barcodes });
  }
  return decoded;
}

function takeBatch() {
  clearTimeout(state.batchTimer);
  state.batchTimer = null;
  const images = state.batch;
  state.batch = [];
  return images;
}

async function finishBatch() {
  const images = takeBatch();
  if (!images.length) return;
  const token = (state.batchToken || 0) + 1;
  state.batchToken = token;
  const preview = await toVisitorScan(images);
  if (token !== state.batchToken || !preview.scanned) return;
  publishScan({ ...preview, message: 'Scan received.' });
  try {
    const decoded = await toVisitorScan(await imagesWithBarcodes(images));
    if (token !== state.batchToken || !decoded.scanned) return;
    if (decoded.fullName || decoded.birthDate) publishScan(decoded);
  } catch (error) {
    if (token === state.batchToken) setStatus('ready', SCANNER_READY_MESSAGE);
  }
}

function queueAutoScanImage(image) {
  state.batch.push(image && typeof image === 'object' ? { ...image, duplexBackFirst: true } : image);
  clearTimeout(state.batchTimer);
  const pages = state.batch.filter((item) => item?.base64Data).length;
  if (pages >= 2) {
    finishBatch();
    return;
  }
  state.batchTimer = setTimeout(() => {
    finishBatch();
  }, 250);
}

let preparePromise = null;
let session = 0;

function beginSession() {
  session += 1;
  return session;
}

function streamIsLive(stream) {
  return Boolean(stream) && stream.readyState !== 2;
}

function scheduleRearm(fetchImpl, sessionId) {
  if (sessionId !== session || state.rearmTimer) return;
  state.rearmTimer = setTimeout(() => {
    state.rearmTimer = null;
    if (sessionId !== session || state.status === 'idle') return;
    armAutoScan(fetchImpl, sessionId).catch(() => {
      if (sessionId === session) setStatus('ready', SCANNER_READY_MESSAGE);
    });
  }, 3000);
}

async function armAutoScan(fetchImpl, sessionId) {
  if (!fetchImpl || sessionId !== session) return;
  await fetchWithTimeout(fetchImpl, `${SERVICE_URL}/api/twain/autoscan/enable`, {
    method: 'POST',
    mode: 'cors',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ parameters: SCAN_PARAMS }),
  }, 8000);
  if (sessionId !== session || streamIsLive(state.stream) || typeof EventSource === 'undefined') return;
  if (state.stream) {
    state.stream.close();
    state.stream = null;
  }
  const stream = new EventSource(`${SERVICE_URL}/api/twain/autoscan/events`, { withCredentials: true });
  state.stream = stream;
  stream.addEventListener('image', (event) => {
    if (sessionId !== session) return;
    try {
      queueAutoScanImage(JSON.parse(event.data));
    } catch (error) {
      setStatus('ready', SCANNER_READY_MESSAGE);
    }
  });
  stream.addEventListener('disabled', () => {
    if (state.stream === stream) {
      stream.close();
      state.stream = null;
    }
    if (sessionId !== session) return;
    setStatus('ready', SCANNER_READY_MESSAGE);
    scheduleRearm(fetchImpl, sessionId);
  });
  stream.onerror = () => {
    if (sessionId !== session || stream.readyState !== EventSource.CLOSED) return;
    if (state.stream === stream) state.stream = null;
    scheduleRearm(fetchImpl, sessionId);
  };
}

async function runPrepare(fetchImpl, sessionId) {
  setStatus('preparing', SCANNER_PREPARING_MESSAGE);
  const online = await serviceIsOnline(fetchImpl);
  if (sessionId !== session) return snapshot();
  if (!online) {
    setStatus('unavailable', SCANNER_UNAVAILABLE_MESSAGE);
    return snapshot();
  }

  try {
    const sourcesResponse = await sendCommand(fetchImpl, 'getSources', null, 4000);
    if (sessionId !== session) return snapshot();
    const sources = sourcesResponse?.data?.availableSources || [];
    const names = sources.map(sourceName).filter(Boolean);
    const chosen = names.find((name) => /ds687|ambir/i.test(name)) || names[0];
    if (!chosen) {
      setStatus('unavailable', SCANNER_UNAVAILABLE_MESSAGE);
      return snapshot();
    }
    await sendCommand(fetchImpl, 'openSource', { sourceName: chosen }, 20000);
    if (sessionId !== session) return snapshot();
    state.sourceName = chosen;
    await armAutoScan(fetchImpl, sessionId);
    if (sessionId !== session) return snapshot();
    setStatus('ready', SCANNER_READY_MESSAGE);
  } catch (error) {
    if (sessionId !== session) return snapshot();
    setStatus('unavailable', SCANNER_UNAVAILABLE_MESSAGE);
  }
  return snapshot();
}

export function prepareIdScanner({ fetchImpl = browserFetch() } = {}) {
  if (state.status === 'ready' && streamIsLive(state.stream)) return Promise.resolve(snapshot());
  if (!fetchImpl) {
    setStatus('unavailable', SCANNER_UNAVAILABLE_MESSAGE);
    return Promise.resolve(snapshot());
  }
  if (!preparePromise) {
    const sessionId = beginSession();
    preparePromise = runPrepare(fetchImpl, sessionId).finally(() => {
      preparePromise = null;
    });
  }
  return preparePromise;
}

export async function scanPreparedId({ fetchImpl = browserFetch() } = {}) {
  if (state.status !== 'ready') await prepareIdScanner({ fetchImpl });
  if (state.status !== 'ready' || !fetchImpl) {
    return {
      available: false,
      scanned: false,
      fullName: '',
      documentToken: null,
      imageDataUrl: null,
      message: SCANNER_UNAVAILABLE_MESSAGE,
    };
  }
  try {
    const response = await sendCommand(fetchImpl, 'scan', SCAN_PARAMS, 60000);
    const scan = await toVisitorScan(await imagesWithBarcodes(
      (response?.data?.images || []).map((image) => ({ ...image, duplexBackFirst: true })),
    ));
    if (scan.scanned) publishScan(scan);
    return scan;
  } catch (error) {
    return {
      available: true,
      scanned: false,
      fullName: '',
      documentToken: null,
      imageDataUrl: null,
      message: error?.message || 'The ID scanner could not read this card. Enter the visitor manually.',
    };
  }
}

export async function releaseIdScanner({ fetchImpl = browserFetch() } = {}) {
  const sessionId = beginSession();
  const wasReady = state.status === 'ready';
  clearTimeout(state.batchTimer);
  clearTimeout(state.rearmTimer);
  state.rearmTimer = null;
  state.batch = [];
  if (state.stream) {
    state.stream.close();
    state.stream = null;
  }
  if (fetchImpl && wasReady) {
    try {
      await fetchWithTimeout(fetchImpl, `${SERVICE_URL}/api/twain/autoscan/disable`, {
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
      }, 1500);
      if (sessionId !== session) return;
      await sendCommand(fetchImpl, 'closeSource', null, 1500);
    } catch (error) {
      if (sessionId !== session) return;
      state.sourceName = '';
    }
  }
  if (sessionId !== session) return;
  state.sourceName = '';
  state.lastScan = null;
  state.openCheckIn = false;
  setStatus('idle', SCANNER_UNAVAILABLE_MESSAGE);
}
